import React, { useEffect, useRef, useState } from 'react'
import { animated, useSpring, easings } from '@react-spring/web'
import QRCode from 'qrcode'
import MarkdownIt from 'markdown-it'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../../store'

import type { MenuItem } from '../../../models/types'
import { ActionType, CurrentSubView, Routes, UIPart } from '../../../models/enums'

import SvgColorChanger from '../../Attachments/Utils/SvgColorChanger'
import CloseButton from '../../Common/CloseButton'
import QRCodeComponent from '../../Utils/QRCodeComponent'

interface DashboardItemProps extends MenuItem {
  onClick: () => void
}

const md = MarkdownIt({
  breaks: true,
  html: true,
  xhtmlOut: true,
  typographer: true,
  quotes: `""''`
})

const springEnterDuration = 500
const springQrcodeDuration = 500

const DashboardItem = ({ index, text, textColor, backgroundColor, image, action, onClick }: DashboardItemProps): JSX.Element => {
  const dispatch = useDispatch()
  const isUsedOnBorne = useSelector((state: RootState) => state.retorikReducer.configuration.isUsedOnBorne)
  const [qrCodeData, setQrCodeData] = useState<string | undefined>(undefined)
  const divRef = useRef<HTMLDivElement>(null)
  const fadeInTimerRef = useRef<NodeJS.Timer | null>(null)

  const [spring, api] = useSpring(() => ({
    from: {
      transform: 'translateX(0%)',
      opacity: 0
    }
  }))

  const [springQr, apiQr] = useSpring(() => ({
    from: {
      opacity: 0
    }
  }))

  useEffect(() => {
    setTimeout(() => {
      api.start({
        from: {
          transform: 'translateX(100%)',
          opacity: 0
        },
        to: {
          transform: 'translateX(0%)',
          opacity: 1
        },
        config: {
          duration: springEnterDuration,
          easing: easings.easeInBack
        }
      })
    }, index * 150)
  }, [])

  const handleInternalAction = (value: Record<string, any> | undefined): void => {
    if (value?.name) {
      switch (value.name.toLowerCase()) {
        case 'openlanguage':
          dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.languages))
          break
        case 'openhistory':
          dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.history))
          break
        case 'opentutorial':
          console.log('Open tutorial')
          break
        case 'opennews':
          dispatch(storeActions.view.setRoute(Routes.News))
          break
        case 'openweather':
          dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.weather))
          break
        case 'openemergency':
          dispatch(storeActions.utils.setCurrentSubView(CurrentSubView.emergency))
          break
        case 'fullscreenimage':
          value.url && dispatch(storeActions.utils.setFullScreenImage(value.url))
          break
      }
    }
  }

  useEffect(() => {
    qrCodeData &&
      apiQr.start({
        from: {
          opacity: 0
        },
        to: {
          opacity: 1
        },
        config: {
          duration: springQrcodeDuration
        }
      })

    return () => {
      fadeInTimerRef?.current && clearTimeout(fadeInTimerRef.current)
    }
  }, [qrCodeData])

  const handleCloseQrCode = (): void => {
    apiQr.start({
      from: {
        opacity: 1
      },
      to: {
        opacity: 0
      },
      config: {
        duration: springQrcodeDuration
      }
    })

    fadeInTimerRef?.current && clearTimeout(fadeInTimerRef.current)
    fadeInTimerRef.current = setTimeout(() => {
      setQrCodeData(undefined)
    }, springQrcodeDuration)
  }

  const setQrCodeDataOverlay = async (url: string): Promise<void> => {
    const qrCodeString = await QRCode.toDataURL(url)
    setQrCodeData(qrCodeString)
  }

  const handleClick = (e: React.MouseEvent): void => {
    switch (action.type) {
      case ActionType.message:
        action.value?.text && dispatch(storeActions.directline.sendMessage({ text: action.value.text }))
        onClick()
        break
      case ActionType.event:
        action.value?.name && dispatch(storeActions.directline.sendEvent({ name: action.value.name, value: action.value.value || null }))
        onClick()
        break
      case ActionType.url:
        if (action.value?.url) {
          // Generate QR-code data on borne, open the URL otherwise
          isUsedOnBorne ? setQrCodeDataOverlay?.(action.value.url) : window.open(action.value.url, '_blank', 'noopener noreferrer')
        }
        break
      case ActionType.internal:
        handleInternalAction(action.value)
        onClick()
        break
    }

    dispatch(storeActions.utils.setClickedOnUI(UIPart.card))
    e.stopPropagation()
    e.preventDefault()
  }

  return (
    <animated.div
      ref={divRef}
      className='rf-relative rf-w-32 tablet:rf-w-28 borne:rf-w-40 rf-aspect-square rf-p-3 borne:rf-p-5 rf-flex rf-flex-col rf-justify-between rf-items-center hover:rf-shadow-[inset_0_0_12px_#00000029] hover:rf-cursor-pointer rf-rounded rf-shadow-[3px_3px_16px_#00000029] rf-overflow-hidden'
      style={{
        color: textColor || '#575F6B',
        background: backgroundColor || 'linear-gradient(to left top, #F2F3F6, #E5E6EC)',
        ...spring
      }}
      onClick={(e) => handleClick(e)}
    >
      {/* Image */}
      {image?.url && (
        <div className='rf-h-[50%] rf-flex rf-justify-center rf-items-center'>
          {image.url.indexOf('svg') > -1 ? <SvgColorChanger svgUrl={image.url} height='100%' fillColor={image.color} /> : <img className='rf-h-full' src={image.url} />}
        </div>
      )}

      {/* Text */}
      <div
        className='rf-h-[35%] rf-w-full rf-flex rf-justify-center rf-items-center rf-text-center rf-text-extra-small-size-auto rf-overflow-y-hidden rf-scrollbar-thin'
        dangerouslySetInnerHTML={{
          __html: md.render(text)
        }}
      />

      {/* QR code */}
      {qrCodeData && (
        <animated.div
          className='rf-absolute rf-h-full rf-w-full rf-top-0 rf-left-0 rf-z-ui rf-flex rf-flex-col rf-justify-center rf-items-center rf-gap-2 rf-p-2 rf-text-truewhite'
          style={{
            background: 'rgba(18,18,18,0.7)',
            ...springQr
          }}
        >
          <CloseButton onClick={handleCloseQrCode} showLabel={false} />

          {/* QR code */}
          <QRCodeComponent src={qrCodeData} />
        </animated.div>
      )}
    </animated.div>
  )
}

export default DashboardItem
